import { useIntlFormatting } from '../../utils/intl';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  ArrowLeft,
  Droplets,
  Gauge,
  Leaf,
  Pencil,
  Sprout,
  Waves,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import SafeParcelControl from '../../components/SafeParcelControl';
import CgmPressureStatus from '../../components/CgmPressureStatus';
import ParcelConfirmDialog from '../../components/ParcelConfirmDialog';
import { useParcelValveCommand } from '../../hooks/useParcelValveCommand';
import { isBusyStatus } from '../../lib/valveCommandSafety';
import SoverdiSectorKpiCardR7 from '../../soverdi-clone/components/SoverdiSectorKpiCardR7';
import { useAgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { useNodeHistory } from '../../hooks/useNodeHistory';
import { parcelValveState } from '../../lib/parcelOperational';
import type { IoTNode, ParcelValveBinding } from '../../lib/iotApi';
import { buildSectorModel } from '../../lib/sectorOperationalModel';
import { getSoverdiStatusInfo, toSoverdiSectorView } from '../../soverdi-clone/adapters/agriSafeDashboardAdapter';
import { scopeAlerts } from '../../soverdi-clone/adapters/dashboardContext';
import {
  MAX_CUSTOM_NAME_LENGTH,
  canRenameSector,
  isValidCustomDisplayName,
  normalizeCustomDisplayName,
} from '../../soverdi-clone/adapters/sectorNaming';
import { useAuth } from '../../contexts/AuthContext';
import {
  SOIL_MOISTURE_ZONES,
  buildHydraulicHistory,
  buildSoilHistory,
  formatHistoryAxis,
  getDefaultDateWindow,
  resolveHistoryWindow,
  type DateWindow,
  type FeedbackRange,
} from '../../soverdi-clone/adapters/sectorHistory';

/** A null metric has no real source — render the em dash, never a fake 0. */
const formatMetric = (value: number | null | undefined, digits: number): string =>
  value === null || value === undefined ? '—' : value.toFixed(digits);

const formatFreshMetric = (
  metric: { value: number | null | undefined; freshness: string },
  digits: number,
  unit: string,
): string => metric.freshness === 'fresh' && metric.value !== null && metric.value !== undefined
  ? `${metric.value.toFixed(digits)} ${unit}`
  : '—';

const RangeSelector: React.FC<{
  value: FeedbackRange;
  onChange: (value: FeedbackRange) => void;
  window: DateWindow;
  onWindowChange: (window: DateWindow) => void;
  valid: boolean;
  invalidMessage: string;
}> = ({ value, onChange, window, onWindowChange, valid, invalidMessage }) => {
  const { t } = useTranslation('common');
  return (
  <div className="flex flex-col gap-2">
    <div className="inline-flex flex-wrap rounded-lg bg-[var(--bg-tertiary)] p-1 w-fit">
      {(['24h', '48h', '7d', '30d', 'custom'] as FeedbackRange[]).map((item) => (
        <button key={item} type="button" onClick={() => onChange(item)} className={`min-h-[36px] px-3 rounded-md text-xs font-semibold ${value === item ? 'bg-[var(--surface)] text-[var(--color-primary)] shadow-sm' : 'text-[var(--text-secondary)]'}`}>
          {{ '24h': t('sectorDetail.24h'), '48h': t('sectorDetail.48h'), '7d': t('sectorDetail.7d'), '30d': t('sectorDetail.30d'), custom: t('sectorDetail.custom') }[item]}
        </button>
      ))}
    </div>
    {value === 'custom' && (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-2">{t('sectorDetail.from')} <input type="date" value={window.start} max={window.end || undefined} aria-invalid={!valid} onChange={(event) => onWindowChange({ ...window, start: event.target.value })} className="min-h-[38px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-2 text-[var(--text-primary)]" /></label>
          <label className="flex items-center gap-2">{t('sectorDetail.to')} <input type="date" value={window.end} min={window.start || undefined} aria-invalid={!valid} onChange={(event) => onWindowChange({ ...window, end: event.target.value })} className="min-h-[38px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-2 text-[var(--text-primary)]" /></label>
        </div>
        {!valid && <p className="text-xs font-medium text-[var(--color-danger)]" role="alert">{invalidMessage}</p>}
      </div>
    )}
  </div>
);
};

/** Truthful placeholder that keeps a chart's footprint without drawing data. */
const ChartUnavailable: React.FC<{ message: string }> = ({ message }) => (
  <div className="h-[330px] w-full flex items-center justify-center" data-testid="chart-unavailable">
    <p className="text-sm text-[var(--text-secondary)]">{message}</p>
  </div>
);

const CgmSectorDetailR7: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const formatChartValue = (value: unknown): string => typeof value === 'number' && Number.isFinite(value) ? formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const { sectorId } = useParams<{ sectorId: string }>();
  const {
    sectors,
    technicalSectors,
    parcelDisplayName,
    topology,
    nodes,
    placement,
    view,
    loading,
    loadError,
    parcelBindings,
    refreshNodes,
    renameSector,
    namesUnavailable,
    bindingsUnavailable,
  } = useAgriSafeSoverdiDashboard();

  const [soilRange, setSoilRange] = useState<FeedbackRange>('24h');
  const [soilWindow, setSoilWindow] = useState<DateWindow>(getDefaultDateWindow);
  const [hydraulicRange, setHydraulicRange] = useState<FeedbackRange>('24h');
  const [hydraulicWindow, setHydraulicWindow] = useState<DateWindow>(getDefaultDateWindow);

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canRename = canRenameSector(user?.role);

  const field = useFieldBindings();
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedId = sectorId ? decodeURIComponent(sectorId) : '';
  const sector = sectors.find((candidate) => candidate.id === decodedId) ?? technicalSectors?.find((candidate) => candidate.id === decodedId) ?? null;

  const commissionedParcels = field.loaded && !field.error && field.farmId === topology?.farmId
    ? field.parcelBindings.filter((binding) => binding.site === sector?.siteName
      && binding.sector_code === sector?.sectorCode && binding.binding_status === 'CONFIRMED') : [];
  const requestedBinding = searchParams.get('binding') ?? sector?.bindingId ?? null;
  const activeParcel = requestedBinding
    ? commissionedParcels.find((binding) => binding.binding_id === requestedBinding)
    : commissionedParcels.length === 1 ? commissionedParcels[0] : undefined;
  const pressureId = activeParcel && field.pressureSensorByBinding?.[activeParcel.binding_id];
  const command = useParcelValveCommand({ role: user?.role, nodes, refreshNodes: refreshNodes ?? (async () => nodes) });
  const [trackedBindings, setTrackedBindings] = useState<ParcelValveBinding[]>([]);
  const commandBusy = trackedBindings.some((binding) => isBusyStatus(command.statusFor(binding.binding_id)));
  const { cancelConfirmation } = command;
  useEffect(() => { cancelConfirmation(); }, [activeParcel?.binding_id, requestedBinding, decodedId, cancelConfirmation]);
  const controls = activeParcel ? [activeParcel] : requestedBinding ? [] : (parcelBindings ?? []).filter((binding) =>
    binding.site === sector?.siteName && binding.sector_code === sector?.sectorCode);
  const trackedControls = trackedBindings.filter((binding) => (field.error || bindingsUnavailable || !controls.some((entry) => entry.binding_id === binding.binding_id))
    && isBusyStatus(command.statusFor(binding.binding_id)));
  const tracking = trackedControls.length > 0 ? <SafeParcelControl displayNameFor={parcelDisplayName} parcels={trackedControls} nodes={nodes} now={Date.now()}
    canCommand={false} statusFor={command.statusFor} outcomeFor={command.outcomeFor} /> : null;
  useEffect(() => { setRenaming(false); }, [activeParcel?.binding_id, requestedBinding, decodedId]);

  const parcelScoped = sector?.siteName === 'Roubia' || requestedBinding !== null;
  const sharedController = activeParcel && field.parcelBindings.filter((binding) =>
    binding.controller_node_id === activeParcel.controller_node_id && binding.binding_status === 'CONFIRMED').length > 1;

  // Rebuild the canonical model for THIS sector so chart series can be tied to
  // the sector's own nodes. Same builder as the Dashboard — one interpretation.
  const model = useMemo(() => {
    if (!sector) return null;
    const result = buildSectorModel({
      nodes,
      placement,
      siteName: sector.siteName,
      sectorCode: sector.sectorCode,
      now: Date.now(),
      ...(parcelScoped ? {
        commissionedNodes: activeParcel ? nodes.filter((node) =>
          (node.id === activeParcel.controller_node_id || node.id === pressureId
            || (commissionedParcels.length === 1 && sector.canonicalParcelNames.length === 1 && node.model === 'soil_air_ecth' && placement.get(node.sector_id ?? '')?.siteName === sector.siteName
              && placement.get(node.sector_id ?? '')?.sectorCode === sector.sectorCode))
          && nodes.filter((candidate) => candidate.id === node.id).length === 1) : [],
      } : {}),
    });
    if (parcelScoped) {
      const state = parcelValveState(result.controller, activeParcel?.channel_id ?? '', Date.now());
      const operationalStates = { open: 'active', closed: 'stopped', unknown: 'unknown', stale: 'unknown' } as const;
      result.operationalState = operationalStates[state];
      result.stateSinceMs = null;
      if (sharedController) {
        result.metrics.flow = { ...result.metrics.flow, value: null, freshness: 'absent' };
      }
    }
    return result;
  }, [sector, nodes, placement, activeParcel, pressureId, parcelScoped, sharedController, requestedBinding, commissionedParcels.length]);

  const soilBounds = useMemo(
    () => resolveHistoryWindow(soilRange, soilWindow),
    [soilRange, soilWindow],
  );
  const hydraulicBounds = useMemo(
    () => resolveHistoryWindow(hydraulicRange, hydraulicWindow),
    [hydraulicRange, hydraulicWindow],
  );

  // Per-node reads, scoped strictly to this sector's assigned equipment.
  const soilRead = useNodeHistory(soilBounds.valid ? (model?.soilSensor?.id ?? null) : null, soilBounds.valid ? soilBounds.hours : 0);
  const pressureRead = useNodeHistory(hydraulicBounds.valid ? (model?.pressureSensor?.id ?? null) : null, hydraulicBounds.valid ? hydraulicBounds.hours : 0);
  const flowRead = useNodeHistory(hydraulicBounds.valid ? (!sharedController ? model?.controller?.id ?? null : null) : null, hydraulicBounds.valid ? hydraulicBounds.hours : 0);

  const soilHistory = useMemo(
    () => buildSoilHistory(soilRead.series, soilBounds),
    [soilRead.series, soilBounds],
  );
  const hydraulicHistory = useMemo(
    () => buildHydraulicHistory(pressureRead.series, flowRead.series, hydraulicBounds),
    [pressureRead.series, flowRead.series, hydraulicBounds],
  );
  const hasServerVolume = !!(activeParcel
    && model?.controller?.parcel_supervision?.[activeParcel.binding_id]?.flow);

  const submitRename = async (): Promise<void> => {
    const normalized = normalizeCustomDisplayName(draft);
    if (!isValidCustomDisplayName(draft) || normalized === null) {
      setRenameError(t('renameInvalid'));
      return;
    }
    if (!sector || activeParcel || requestedBinding) return;
    setSaving(true);
    setRenameError(null);
    try {
      await renameSector(sector.siteName, sector.sectorCode, normalized);
      setRenaming(false);
    } catch {
      // Never optimistically show a name the server did not accept: the panel
      // stays open with the previous effective name still rendered behind it.
      setRenameError(t('renameError'));
    } finally {
      setSaving(false);
    }
  };

  const submitReset = async (): Promise<void> => {
    if (!sector || activeParcel || requestedBinding) return;
    setSaving(true);
    setRenameError(null);
    try {
      // Reset clears the override; it never copies the canonical name into it.
      await renameSector(sector.siteName, sector.sectorCode, null);
      setRenaming(false);
    } catch {
      setRenameError(t('renameError'));
    } finally {
      setSaving(false);
    }
  };

  // Only alerts whose provenance canonically resolves to THIS sector.
  const sectorAlerts = useMemo(() => {
    if (!sector) return [];
    if (parcelScoped) {
      const sources = [model?.pressureSensor, ...(!sharedController ? [model?.controller] : [])]
        .filter((node): node is IoTNode => Boolean(node));
      return (view?.alerts ?? []).filter((alert) => !alert.acknowledged && sources.some((source) =>
        (alert.source === source.id || alert.source === source.name)
        && nodes.filter((node) => node.id === alert.source || node.name === alert.source).length === 1
      )).map((alert) => ({ alert }));
    }
    return scopeAlerts({
      alerts: view?.alerts ?? [],
      nodes,
      placement,
      siteName: sector.siteName,
    }).filter((entry) => entry.sectorId === sector.id && !entry.alert.acknowledged);
  }, [sector, view, nodes, placement, parcelScoped, model, sharedController]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;

  if ((loadError && !topology) || field.error || bindingsUnavailable || !sector || !model) {
    return (
      <div className="space-y-4" data-testid="sector-detail-unavailable">
        <button onClick={() => navigate('/sectors')} className="inline-flex items-center gap-2 text-[var(--color-primary)]"><ArrowLeft className="w-4 h-4" />{t('sectorDetail.back')}</button>
        <div className="p-8 rounded-xl border border-[var(--border-light)] text-center">
          {loadError || field.error || bindingsUnavailable ? t('availabilityUnavailable') : t('sectorDetail.missing')}
        </div>
        {tracking}
      </div>
    );
  }

  const view7 = toSoverdiSectorView(model, sector.siteName, sector.sectorCode, {
    parcelNames: activeParcel ? [parcelDisplayName?.(activeParcel) ?? activeParcel.parcel] : sector.canonicalParcelNames,
    customDisplayName: activeParcel ? null : sector.customDisplayName,
  });
  const status = getSoverdiStatusInfo(view7.status);

  const soilUnavailable = soilHistory.length === 0;
  const hydraulicUnavailable = hydraulicHistory.length === 0;
  const chartMessage = (read: { error: boolean }, hasNode: boolean): string => {
    if (read.error) return t('availabilityUnavailable');
    if (!hasNode) return t('availabilityNotReported');
    return t('availabilityUnavailable');
  };

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="soverdi-sector-detail-r7">
      <button onClick={() => navigate('/sectors')} className="inline-flex items-center gap-2 min-h-[44px] text-sm font-semibold text-[var(--color-primary)]"><ArrowLeft className="w-4 h-4" />{t('sectorDetail.back')}</button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {/* Primary title = effective business name. The technical identity
                below it never changes, whatever the sector is called. */}
            <h1 className="text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{view7.displayName}</h1>
            {canRename && !activeParcel && !requestedBinding && !renaming && (
              <button
                type="button"
                onClick={() => { setRenaming(true); setDraft(view7.customDisplayName ?? ''); setRenameError(null); }}
                aria-label={t('rename')}
                title={t('rename')}
                data-testid="sector-rename-open"
                className="min-h-[32px] px-2 rounded-lg border border-[var(--border-light)] text-[var(--text-secondary)] hover:text-[var(--color-primary)]"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t('sectorTechnicalLabel', { site: view7.siteName, code: view7.sectorCode })}
          </p>
        </div>
        {!activeParcel && <span className="w-fit px-3 py-1.5 rounded-full text-xs font-semibold" style={{ color: status.color }}>{status.label}</span>}
      </div>

      {commissionedParcels.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {commissionedParcels.map((binding) => (
            <button key={binding.binding_id} type="button" aria-pressed={activeParcel?.binding_id === binding.binding_id}
              disabled={commandBusy || Boolean(command.pending)}
              className="min-h-[44px] rounded-lg border px-4"
              onClick={() => setSearchParams({ binding: binding.binding_id })}>{parcelDisplayName?.(binding) ?? binding.parcel}</button>
          ))}
        </div>
      )}
      {renaming && !activeParcel && !requestedBinding && (
        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 space-y-3" data-testid="sector-rename-panel">
          {namesUnavailable && (
            <p className="text-xs text-[var(--color-warning)]" data-testid="sector-rename-unavailable">{t('renameUnavailable')}</p>
          )}
          <div>
            <h2 className="font-semibold text-[var(--text-primary)]">{t('sectorName')}</h2>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {t('originalName')}: {view7.canonicalDisplayName ?? view7.name}
            </p>
          </div>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">
            {t('customName')}
            <input
              type="text"
              value={draft}
              maxLength={MAX_CUSTOM_NAME_LENGTH}
              onChange={(event) => setDraft(event.target.value)}
              data-testid="sector-rename-input"
              className="mt-1 w-full min-h-[44px] rounded-lg border border-[var(--border-light)] bg-[var(--surface)] px-3 text-[var(--text-primary)]"
            />
          </label>
          {renameError && (
            <p className="text-xs text-[var(--color-danger)]" data-testid="sector-rename-error">{renameError}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={submitRename} disabled={saving} data-testid="sector-rename-save" className="min-h-[42px] px-4 rounded-lg bg-[var(--color-primary)] text-white text-sm font-semibold disabled:opacity-40">{t('saveName')}</button>
            <button type="button" onClick={() => { setRenaming(false); setRenameError(null); }} disabled={saving} data-testid="sector-rename-cancel" className="min-h-[42px] px-4 rounded-lg border border-[var(--border-light)] text-sm font-semibold text-[var(--text-primary)] disabled:opacity-40">{t('cancelRename')}</button>
            {!namesUnavailable && view7.customDisplayName !== null && (
              <button type="button" onClick={submitReset} disabled={saving} data-testid="sector-rename-reset" className="min-h-[42px] px-4 rounded-lg border border-[var(--border-light)] text-sm font-semibold text-[var(--text-secondary)] disabled:opacity-40">{t('resetName')}</button>
            )}
          </div>
        </section>
      )}

      {activeParcel ? (
        <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4" data-testid="parcel-irrigation-summary">
          <p data-testid="parcel-current-flow">{t('sectorDetail.flow')}: {formatFreshMetric({ ...model.metrics.flow, value: view7.flowM3h }, 2, 'm³/h')}</p>
          <CgmPressureStatus key={activeParcel.binding_id} node={nodes.filter((node) => node.id === pressureId).length === 1 ? nodes.find((node) => node.id === pressureId) : undefined}
            valveState={parcelValveState(model.controller, activeParcel.channel_id, Date.now())} detailed />
        </section>
      ) : <SoverdiSectorKpiCardR7 sector={view7} showSoilMoisture />}

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5 space-y-4" data-testid="semantic-parcel-controls">
        <div>
          <h2 className="font-semibold text-[var(--text-primary)]">{t('sectorDetail.parcel')}</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t('sectorDetail.parcelHelp')}
          </p>
        </div>
        {sectorAlerts.length > 0 && <div className="rounded-xl border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]" data-testid="sector-detail-alerts">{sectorAlerts.map((entry) => entry.alert.message).join(' · ')}</div>}
        <SafeParcelControl
          parcels={controls}
          displayNameFor={parcelDisplayName}
          nodes={nodes}
          now={Date.now()}
          canCommand={!commandBusy}
          statusFor={command.statusFor}
          outcomeFor={command.outcomeFor}
          onCommand={(binding, action) => {
            if (commandBusy) return;
            setTrackedBindings((previous) => [...previous.filter((entry) => entry.binding_id !== binding.binding_id), binding]);
            command.requestCommand(binding, action);
          }}
        />
        {tracking}
        {command.pending && <ParcelConfirmDialog displayName={parcelDisplayName?.(command.pending.binding)} pending={command.pending} onCancel={command.cancelConfirmation} onConfirm={command.confirmCommand} />}
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div><div className="flex items-center gap-2"><Sprout className="w-5 h-5 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">{t('sectorDetail.soilTitle')}</h2></div><p className="text-sm text-[var(--text-secondary)] mt-1">{t('sectorDetail.zones')}</p></div>
          <RangeSelector value={soilRange} onChange={setSoilRange} window={soilWindow} onWindowChange={setSoilWindow} valid={soilBounds.valid} invalidMessage={t('invalidDateWindow')} />
        </div>
        <div className="flex flex-wrap gap-3 text-xs">{SOIL_MOISTURE_ZONES.slice().reverse().map((zone) => <span key={zone.key} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: zone.color }} />{{ danger: t('sectorDetail.danger'), warning: t('sectorDetail.warning'), comfort: t('sectorDetail.comfort'), excess: t('sectorDetail.excess') }[zone.key]} ({zone.min}-{zone.max} %)</span>)}</div>
        {soilUnavailable ? (
          <ChartUnavailable message={chartMessage(soilRead, Boolean(model.soilSensor))} />
        ) : (
          <div className="h-[330px] w-full"><ResponsiveContainer width="100%" height="100%"><LineChart data={soilHistory} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}><CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" vertical={false} /><XAxis dataKey="timestamp" tickFormatter={(value) => formatHistoryAxis(String(value), soilRange)} minTickGap={24} tick={{ fontSize: 11 }} /><YAxis tickFormatter={formatChartValue} yAxisId="soil" domain={[0, 100]} unit=" %" width={52} tick={{ fontSize: 11 }} />{SOIL_MOISTURE_ZONES.map((zone) => <ReferenceArea key={zone.key} yAxisId="soil" y1={zone.min} y2={zone.max} fill={zone.color} fillOpacity={0.12} strokeOpacity={0} />)}<Tooltip labelFormatter={(label) => formatDateTime(String(label))} formatter={(value) => [`${formatChartValue(value)} %`, t('sectorDetail.soil')] as [string, string]} /><Line yAxisId="soil" type="monotone" dataKey="soilMoisture_percent" name={t('sectorDetail.soil')} stroke="#0f172a" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls={false} /></LineChart></ResponsiveContainer></div>
        )}
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div><div className="flex items-center gap-2"><Waves className="w-5 h-5 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">{t('sectorDetail.hydraulic')}</h2></div><p className="text-sm text-[var(--text-secondary)] mt-1">{hasServerVolume ? t('supervisionSharedWarning') : t('sectorDetail.volumeHelp')}</p></div>
          <RangeSelector value={hydraulicRange} onChange={setHydraulicRange} window={hydraulicWindow} onWindowChange={setHydraulicWindow} valid={hydraulicBounds.valid} invalidMessage={t('invalidDateWindow')} />
        </div>
        {hydraulicUnavailable ? (
          <ChartUnavailable message={chartMessage(pressureRead, Boolean(model.pressureSensor || model.controller))} />
        ) : (
          <div className="h-[330px] w-full"><ResponsiveContainer width="100%" height="100%"><LineChart data={hydraulicHistory} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}><CartesianGrid stroke="var(--border-light)" strokeDasharray="3 3" vertical={false} /><XAxis dataKey="timestamp" tickFormatter={(value) => formatHistoryAxis(String(value), hydraulicRange)} minTickGap={24} tick={{ fontSize: 11 }} /><YAxis tickFormatter={formatChartValue} yAxisId="hydraulic" width={52} tick={{ fontSize: 11 }} domain={[0, 'auto']} /><YAxis tickFormatter={formatChartValue} yAxisId="volume" orientation="right" width={58} tick={{ fontSize: 11 }} domain={[0, 'auto']} /><Tooltip formatter={(value, name) => [formatChartValue(value), name]} labelFormatter={(label) => formatDateTime(String(label))} /><Legend /><Line yAxisId="hydraulic" type="monotone" dataKey="pressure_bar" name={t('sectorDetail.pressure')} stroke="#2563eb" strokeWidth={2.3} dot={false} connectNulls={false} /><Line yAxisId="hydraulic" type="monotone" dataKey="flow_m3h" name={t('sectorDetail.flow')} stroke="#22c55e" strokeWidth={2.3} dot={false} connectNulls={false} /><Line yAxisId="volume" type="monotone" dataKey="volume_m3" name={t('sectorDetail.volume')} stroke="#8b5cf6" strokeWidth={2.3} dot={false} connectNulls={false} /></LineChart></ResponsiveContainer></div>
        )}
      </section>

      {!activeParcel && <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] overflow-hidden">
        <div className="p-4 border-b border-[var(--border-light)]"><h2 className="font-semibold text-[var(--text-primary)]">{t('sectorDetail.last')}</h2></div>
        {/*
          No completed irrigation run can be canonically tied to a sector: a run
          carries farm_id + program_id only, and nothing transitions a run out of
          SCHEDULED today. Inventing history here is forbidden.
        */}
        <div className="p-6 text-center" data-testid="irrigation-history-unavailable">
          <p className="text-sm text-[var(--text-secondary)]">{t('availabilityUnavailable')}</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('availabilityNoProgramming')}</p>
        </div>
      </section>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-3"><Droplets className="w-4 h-4 text-[var(--color-primary)]" /><div className="text-xs text-[var(--text-secondary)] mt-2">{t('sectorDetail.soil')}</div><div className="font-semibold" data-testid="detail-soil-moisture">{formatFreshMetric(model.metrics.soilMoisture, 0, '%')}</div></div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-3"><Leaf className="w-4 h-4 text-[var(--color-primary)]" /><div className="text-xs text-[var(--text-secondary)] mt-2">{t('sectorDetail.ec')}</div><div className="font-semibold" data-testid="detail-soil-ec">{formatFreshMetric(model.metrics.soilEc, 0, 'µS/cm')}</div></div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-3"><Gauge className="w-4 h-4 text-[var(--color-primary)]" /><div className="text-xs text-[var(--text-secondary)] mt-2">{t('sectorDetail.temp')}</div><div className="font-semibold" data-testid="detail-soil-temp">{formatFreshMetric(model.metrics.soilTemperature, 1, '°C')}</div></div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-3"><Activity className="w-4 h-4 text-[var(--color-primary)]" /><div className="text-xs text-[var(--text-secondary)] mt-2">{t('sectorDetail.air')}</div><div className="font-semibold" data-testid="detail-air-humidity">{formatFreshMetric(model.metrics.airHumidity, 0, '%')}</div></div>
      </div>

      <p className="text-xs text-center text-[var(--text-tertiary)]">{t('sectorDetail.footer')}</p>
    </div>
  );
};

export default CgmSectorDetailR7;
